import { useOffline } from '../contexts/OfflineContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Calendar,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Download,
  FileImage,
  History,
  Info,
  ListTodo,
  LocateFixed,
  MapPin,
  Maximize2,
  MessageSquare,
  Minimize2,
  PauseCircle,
  RotateCcw,
  Shield,
  ShieldAlert,
  Trash2,
  X,
  User2,
  UserCheck,
  UserCog,
  Users,
  XCircle,
} from 'lucide-react';
import DatePicker from '../components/DatePicker';
import Dropdown from '../components/Dropdown';
import FloatingField from '../components/FloatingField';
import MapCurrentLocationButton from '../components/MapCurrentLocationButton';
import ModalShell from '../components/ModalShell';
import ProtectedImage, { CircularProgress } from '../components/ProtectedImage';
import RouteSuggestions from '../components/RouteSuggestions';
import TimeSlotPicker from '../components/TimeSlotPicker';
import AddressWithoutStreetConfirmModal from '../components/AddressWithoutStreetConfirmModal';
import { ConfirmModal } from '../components/ConfirmModal';
import useAuthUserType from '../hooks/useAuthUserType';
import useToast from '../hooks/useToast';
import {
  addTaskComment,
  assignTask,
  cancelTask,
  completeTask,
  deferTask,
  deleteTaskPhoto,
  getTask,
  getTaskAssignees,
  getTaskComments,
  getTaskHistory,
  getTaskPhotos,
  markTaskCommentRead,
  resolveDeferredTask,
  takeTask,
  updateTask,
  uploadTaskPhoto,
} from '../services/tasksApi';
import { API_URL } from '../config';
import { safeFetch } from '../utils/safeFetch';
import { compressImageFile } from '../utils/imageCompression';
import { fetchProtectedBlob } from '../utils/protectedMedia';
import { readOfflineRouteItem, shouldUseOfflineDetailFallback } from '../utils/offlineRoutesCache';
import { formatTimezoneDate } from '../utils/timezone';
import { haptic } from '../utils/vibration';
import { MapLoadingSkeleton } from '../components/Skeleton';
import { CURRENT_LOCATION_MAP_ZOOM, replaceCurrentLocationMarker } from '../utils/mapCurrentLocation';
import { getYandexAddressUrl, getYandexMapsUrl, getYandexNavigatorUrl, loadYandexMaps, loadYandexMapsV3, suppressYandexOpenMapsButton } from '../utils/yandexMaps';
import { geocodeAddressWithNominatim, mergeAddressSuggestionCoordinates, resolvePreferredAddressCoordinates } from '../utils/addressCoordinates';
import { getServiceAddressValidation, incompleteServiceAddressMessage, isCompleteServiceAddress } from '../utils/serviceAddressValidation';
import MapProviderToggle, { getStoredMapProvider } from '../components/MapProviderToggle';
import yaMapsIcon from '../assets/images/ya_maps___.svg';
import yandexNavigatorIcon from '../assets/images/yandex_navigator_.svg';
import dgisIcon from '../assets/images/dgis_icon.svg';
import { getDgisAddressUrl, getDgisRouteUrl } from '../utils/dgisMaps';
import L from 'leaflet';

const DETAIL_PAGE_BOTTOM_COMPENSATION = 'calc(-1 * max(4rem, calc(max(env(safe-area-inset-bottom, 0px), var(--app-visual-viewport-bottom-inset, 0px)) + 0.75rem)))';

const buildOfflineTaskFromRouteItem = (routeItem) => {
  const taskId = Number(routeItem?.order_id) || 0;
  return {
    id: taskId,
    task_number: Number(routeItem?.order_number) || routeItem?.order_number || taskId,
    status: routeItem?.status || 'assigned',
    status_label: routeItem?.lifecycle_label || 'Назначено',
    created_date: routeItem?.updated_at || routeItem?.scheduled_date || new Date().toISOString(),
    scheduled_date: routeItem?.scheduled_date || null,
    scheduled_date_key: routeItem?.scheduled_date_key || '',
    time_slot: routeItem?.time_slot || '',
    address_text: routeItem?.address_text || '',
    latitude: routeItem?.latitude ?? null,
    longitude: routeItem?.longitude ?? null,
    description: routeItem?.description || '',
    requires_photo: false,
    assigned_to: routeItem?.assigned_to ?? null,
    assigned_to_name: routeItem?.assignee_name || '',
    created_by: 0,
    created_by_name: '',
    completed_at: null,
    completed_by: null,
    completed_by_name: '',
    completed_by_actor: null,
    completed_by_actor_name: '',
    cancelled_at: null,
    cancelled_by: null,
    cancelled_by_name: '',
    cancel_reason: '',
    photos_count: 0,
    comments_count: 0,
    unread_comments_count: 0,
    can_take: false,
    can_assign: false,
    can_complete: false,
    can_cancel: false,
    can_edit_dispatch: false,
    can_comment: false,
    can_upload_photos: false,
  };
};

const STATUS_META = {
  available: { label: 'Доступно', icon: Users, className: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  assigned: { label: 'Назначено', icon: User2, className: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/25 dark:text-blue-300' },
  deferred: { label: 'Отложено', icon: PauseCircle, className: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/25 dark:text-amber-300' },
  completed: { label: 'Выполнено', icon: CheckCircle2, className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/25 dark:text-emerald-300' },
  cancelled: { label: 'Отменено', icon: XCircle, className: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/25 dark:text-red-300' },
};

const ACTION_LABELS = {
  task_created: 'Создано поручение',
  task_assigned: 'Назначен исполнитель',
  task_reassigned: 'Исполнитель изменён',
  task_unassigned: 'Исполнитель снят',
  task_taken: 'Поручение взято в работу',
  task_dispatch_updated: 'Обновлены дата, адрес или описание',
  task_completed: 'Поручение выполнено',
  task_completed_without_photo_override: 'Закрыто без фото диспетчером',
  task_cancelled: 'Поручение отменено',
  task_deferred: 'Поручение отложено',
  task_deferred_resolved: 'Отложенное поручение обработано',
  task_comment_added: 'Добавлен комментарий',
  task_photo_added: 'Добавлено фото',
};

const TASK_MAP_DEFAULT_ZOOM = 15;
const TASK_MAP_3D_TILT = (40 * Math.PI) / 180;
const UNASSIGN_TASK_ASSIGNEE_VALUE = '__none__';
const TASK_DEFERRED_REASON_OPTIONS = [
  { value: 'assignee_unavailable', label: 'Исполнитель не может выполнить' },
  { value: 'access_problem', label: 'Нет доступа или условий для выполнения' },
  { value: 'missing_information', label: 'Не хватает информации' },
  { value: 'schedule_change', label: 'Нужно перенести срок' },
  { value: 'dependency', label: 'Зависит от другого поручения' },
  { value: 'technical_problem', label: 'Техническая проблема' },
  { value: 'other', label: 'Другая причина' },
];
const DISPATCHER_ICON_BUTTON_BASE = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors disabled:opacity-50';

const TASK_PAGE_ACCENTS = {
  available: {
    text: 'text-slate-600 dark:text-slate-300',
    border: 'border-slate-200 dark:border-slate-800',
  },
  assigned: {
    text: 'text-blue-600 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-900',
  },
  deferred: {
    text: 'text-amber-600 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-900',
  },
  completed: {
    text: 'text-emerald-600 dark:text-emerald-300',
    border: 'border-emerald-200 dark:border-emerald-900',
  },
  cancelled: {
    text: 'text-red-600 dark:text-red-300',
    border: 'border-red-200 dark:border-red-900',
  },
};

const getSuggestionAddressText = (suggestion) => suggestion?.value || suggestion?.unrestricted_value || '';

const formatDateValue = (value) => {
  if (!value) return 'Не указано';
  const [year, month, day] = String(value).split('-');
  return year && month && day ? `${day}.${month}.${year}` : value;
};

const getTaskPhotoUrl = (photo, variant = 'file') => {
  if (!photo?.id || !photo?.task_id) return '';
  return `${API_URL}/api/tasks/${photo.task_id}/photos/${photo.id}/${variant === 'thumbnail' ? 'thumbnail' : 'file'}`;
};

const parsePayload = (value) => {
  if (!value) return null;
  try {
    return typeof value === 'string' ? JSON.parse(value) : value;
  } catch {
    return value;
  }
};

const formatPayload = (value) => {
  const parsed = parsePayload(value);
  if (!parsed) return '';
  if (typeof parsed === 'string') return parsed;
  if (typeof parsed !== 'object') return String(parsed);
  const labels = {
    assigned_to_name: 'Исполнитель',
    scheduled_date: 'Дата',
    time_slot: 'Время',
    address_text: 'Адрес',
    description: 'Описание',
    requires_photo: 'Фото обязательно',
    cancel_reason: 'Причина',
    reason: 'Причина переноса',
    comment: 'Комментарий',
    status: 'Статус',
    resolution: 'Решение',
    previous_assigned_to: 'Было у',
    completed_by_name: 'Засчитано',
    completed_by_actor_name: 'Закрыл',
    text: 'Комментарий',
    caption: 'Подпись',
  };
  const hiddenKeys = new Set([
    'assigned_to',
    'completed_by',
    'completed_by_actor',
    'task_number',
    'comment_id',
    'photo_id',
    'latitude',
    'reason_code',
    'longitude',
    'lat',
    'lng',
    'geo_lat',
    'geo_lon',
  ]);
  const valueLabels = {
    available: 'Доступно',
    assigned: 'Назначено',
    deferred: 'Отложено',
    cancelled: 'Отменено',
    completed: 'Выполнено',
  };
  return Object.entries(parsed)
    .filter(([key]) => !hiddenKeys.has(String(key)))
    .map(([key, currentValue]) => `${labels[key] || key}: ${typeof currentValue === 'boolean' ? (currentValue ? 'Да' : 'Нет') : (valueLabels[currentValue] || currentValue || '—')}`)
    .join(' • ');
};

const AccordionSection = ({ title, icon: Icon, count, unread, open, onToggle, children, contentClassName = 'p-4', className = '', titleClassName = '', iconClassName = '', countClassName = '' }) => {
  const resolvedChildren = open ? (typeof children === 'function' ? children() : children) : null;

  return (
    <section className={`-mx-3 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:-mx-1 ${className}`}>
      <button
        type="button"
        onClick={() => {
          haptic(open ? 'close' : 'open');
          onToggle();
        }}
        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left"
      >
        <span className={`flex min-w-0 items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 ${titleClassName}`}>
          {Icon ? <Icon className={`h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400 ${iconClassName}`} /> : null}
          <span className="truncate">{title}</span>
          {count !== undefined ? (
            <span className={`rounded-lg bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300 ${countClassName}`}>
              {count}
            </span>
          ) : null}
          {unread ? <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500 ring-2 ring-red-100 dark:ring-red-950" /> : null}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? <div className={`border-t border-gray-100 dark:border-gray-800 ${contentClassName}`}>{resolvedChildren}</div> : null}
    </section>
  );
};

const TaskDetailSkeleton = () => (
  <div className="space-y-3">
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="h-[210px] animate-pulse bg-gradient-to-br from-gray-100 via-gray-50 to-gray-200 dark:from-gray-800 dark:via-gray-800/80 dark:to-gray-700 sm:h-[240px]" />
      <div className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <div className="h-6 w-24 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-6 w-28 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <div className="h-4 w-40 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-36 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>
          <div className="space-y-2">
            <div className="ml-auto h-4 w-32 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="ml-auto h-4 w-24 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-4 w-full animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-2/3 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    </section>

    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="space-y-3 p-4">
        <div className="h-5 w-36 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="h-5 w-full animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
        <div className="h-5 w-5/6 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
      </div>
    </section>

    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="space-y-3 p-4">
        <div className="h-5 w-28 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="h-12 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
        <div className="h-12 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
      </div>
    </section>
  </div>
);

const TaskDetail = () => {
  const offline = useOffline();
  const connectionUnavailable = ['offline', 'server_unavailable'].includes(offline?.networkStatus);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapMarkerRef = useRef(null);
  const currentLocationCoordsRef = useRef(null);
  const currentLocationMarkerRef = useRef(null);
  const mapEngineRef = useRef('v3');
  const forceLegacyTaskMapRef = useRef(false);
  const mapLocationRef = useRef({
    center: null,
    zoom: TASK_MAP_DEFAULT_ZOOM,
    azimuth: 0,
    tilt: 0,
  });
  const { toast, showToast, hideToast } = useToast(3400);
  const [mapProvider, setMapProvider] = useState(getStoredMapProvider);
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [history, setHistory] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadingPlaceholder, setUploadingPlaceholder] = useState(null);
  const uploadingPhotoRef = useRef(false);
  const [dispatchForm, setDispatchForm] = useState({ scheduled_date: '', time_slot: '', address_text: '', description: '', requires_photo: false });
  const [assigneeDraft, setAssigneeDraft] = useState(UNASSIGN_TASK_ASSIGNEE_VALUE);
  const [expandedSections, setExpandedSections] = useState({
    dispatcher: true,
    comments: false,
    photos: false,
    history: false,
  });
  const [yandexApiKey, setYandexApiKey] = useState('');
  const [resolvedMapCoords, setResolvedMapCoords] = useState(null);
  const [isResolvingMapCoords, setIsResolvingMapCoords] = useState(false);
  const [isInitializingTaskMap, setIsInitializingTaskMap] = useState(false);
  const [isTaskMapFullscreen, setIsTaskMapFullscreen] = useState(false);
  const [taskMapDisclosure, setTaskMapDisclosure] = useState({ key: '', expanded: true });
  const [taskMapEverOpenedKey, setTaskMapEverOpenedKey] = useState('');
  const [isTaskMapTilted, setIsTaskMapTilted] = useState(false);
  const [isTaskMap3DSupported, setIsTaskMap3DSupported] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeferModal, setShowDeferModal] = useState(false);
  const [showDeferredResolveModal, setShowDeferredResolveModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showCompleteOverrideModal, setShowCompleteOverrideModal] = useState(false);
  const [showCompleteConfirmModal, setShowCompleteConfirmModal] = useState(false);
  const [dispatcherModeEnabled, setDispatcherModeEnabled] = useState(false);
  const [cancelReasonDraft, setCancelReasonDraft] = useState('');
  const [deferDraft, setDeferDraft] = useState({ reason_code: '', comment: '' });
  const [deferredResolveDraft, setDeferredResolveDraft] = useState({
    resolution: 'available',
    scheduled_date: '',
    time_slot: '',
    assigned_to: '',
  });
  const [deferredRouteSuggestions, setDeferredRouteSuggestions] = useState([]);
  const [deferredRouteSuggestionsLoading, setDeferredRouteSuggestionsLoading] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressWithoutStreetSuggestion, setAddressWithoutStreetSuggestion] = useState(null);
  const addressCoordinateRequestRef = useRef(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const { userType: currentUserType } = useAuthUserType();
  const canDeletePhotos = currentUserType === 'admin' || currentUserType === 'developer';
  const addressRequestIdRef = useRef(0);

  const syncCurrentLocationMarker = useCallback((coordinates = currentLocationCoordsRef.current) => {
    currentLocationMarkerRef.current = replaceCurrentLocationMarker({
      map: mapInstanceRef.current,
      engine: mapEngineRef.current,
      previousMarker: currentLocationMarkerRef.current,
      coordinates,
    });
  }, []);

  const backRoute = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('from') === 'deferred') {
      return '/my-orders?tab=my&stage=deferred&deferred_tab=tasks';
    }
    const tab = params.get('tab') || '';
    return tab ? `/tasks?tab=${tab}` : '/tasks';
  }, [location.search]);
  const currentUserId = useMemo(() => Number(localStorage.getItem('user_id') || 0), []);
  const taskMapDisclosureKey = String(task?.id || id || '');
  const isTaskMapExpanded = taskMapDisclosure.key === taskMapDisclosureKey
    ? taskMapDisclosure.expanded
    : task?.status !== 'deferred';
  const taskMapHasBeenOpened = taskMapEverOpenedKey === taskMapDisclosureKey;
  useEffect(() => {
    if (isTaskMapExpanded && !taskMapHasBeenOpened) {
      setTaskMapEverOpenedKey(taskMapDisclosureKey);
    }
  }, [isTaskMapExpanded, taskMapDisclosureKey, taskMapHasBeenOpened]);

  const loadBundle = useCallback(async (silent = false) => {
    if (!id) return;
    let hasCached = false;
    if (!silent) {
      try {
        const cached = await readOfflineRouteItem('task', id);
        if (cached?.item) {
          const offlineTask = cached.detail || buildOfflineTaskFromRouteItem(cached.item);
          setTask(offlineTask);
          setComments([]);
          setPhotos([]);
          setHistory([]);
          setDispatchForm({
            scheduled_date: String(offlineTask.scheduled_date_key || '').slice(0, 10),
            time_slot: offlineTask.time_slot || '',
            address_text: offlineTask.address_text || '',
            description: offlineTask.description || '',
            requires_photo: false,
          });
          setAssigneeDraft(offlineTask.assigned_to ? String(offlineTask.assigned_to) : UNASSIGN_TASK_ASSIGNEE_VALUE);
          setCancelReasonDraft('');
          hasCached = true;
        }
      } catch (cacheErr) {
        // ignore
      }
      if (!hasCached) {
        setLoading(true);
      }
    } else {
      setRefreshing(true);
    }
    setError('');
    try {
      if (connectionUnavailable || navigator.onLine === false) throw new Error('Нет соединения с сервером');
      const [taskData, commentsData, photosData, historyData] = await Promise.all([
        getTask(id),
        getTaskComments(id).catch(() => []),
        getTaskPhotos(id).catch(() => []),
        getTaskHistory(id).catch(() => []),
      ]);
      setTask(taskData);
      setComments(Array.isArray(commentsData) ? commentsData : []);
      setPhotos(Array.isArray(photosData) ? photosData : []);
      setHistory(Array.isArray(historyData) ? historyData : []);
      setDispatchForm({
        scheduled_date: String(taskData?.scheduled_date_key || '').slice(0, 10),
        time_slot: taskData?.time_slot || '',
        address_text: taskData?.address_text || '',
        description: taskData?.description || '',
        requires_photo: Boolean(taskData?.requires_photo),
      });
      setAssigneeDraft(taskData?.assigned_to ? String(taskData.assigned_to) : UNASSIGN_TASK_ASSIGNEE_VALUE);
      setCancelReasonDraft(taskData?.cancel_reason || '');
    } catch (requestError) {
      if (!hasCached) {
        if (shouldUseOfflineDetailFallback(requestError)) {
          const cached = await readOfflineRouteItem('task', id).catch(() => null);
          if (cached?.item) {
            const offlineTask = cached.detail || buildOfflineTaskFromRouteItem(cached.item);
            setTask(offlineTask);
            setComments([]);
            setPhotos([]);
            setHistory([]);
            setDispatchForm({
              scheduled_date: String(offlineTask.scheduled_date_key || '').slice(0, 10),
              time_slot: offlineTask.time_slot || '',
              address_text: offlineTask.address_text || '',
              description: offlineTask.description || '',
              requires_photo: false,
            });
            setAssigneeDraft(offlineTask.assigned_to ? String(offlineTask.assigned_to) : UNASSIGN_TASK_ASSIGNEE_VALUE);
            setCancelReasonDraft('');
            return;
          }
        }
        setTask(null);
        setComments([]);
        setPhotos([]);
        setHistory([]);
        setError(requestError.message || 'Не удалось загрузить поручение');
      }
    } finally {
      silent ? setRefreshing(false) : setLoading(false);
    }
  }, [id, connectionUnavailable]);

  useEffect(() => { loadBundle(); }, [loadBundle]);
  useEffect(() => {
    if (task?.can_assign || task?.can_resolve_deferred) {
      getTaskAssignees().then((data) => setAssignees(Array.isArray(data) ? data : [])).catch(() => setAssignees([]));
    }
  }, [task?.can_assign, task?.can_resolve_deferred]);
  useEffect(() => {
    if (!showDeferredResolveModal || task?.status !== 'deferred' || !String(task?.address_text || '').trim()) {
      setDeferredRouteSuggestions([]);
      setDeferredRouteSuggestionsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({
      stage_name: 'delivery',
      address_text: String(task.address_text).trim(),
      days: '10',
      limit: '3',
    });
    if (Number.isFinite(Number(task.latitude)) && Number.isFinite(Number(task.longitude))) {
      params.set('latitude', String(task.latitude));
      params.set('longitude', String(task.longitude));
    }
    setDeferredRouteSuggestionsLoading(true);
    fetch(`${API_URL}/api/orders/route-suggestions?${params.toString()}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Не удалось получить подходящие варианты');
        return response.json();
      })
      .then((data) => setDeferredRouteSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []))
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setDeferredRouteSuggestions([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setDeferredRouteSuggestionsLoading(false);
      });

    return () => controller.abort();
  }, [showDeferredResolveModal, task?.address_text, task?.latitude, task?.longitude, task?.status]);
  useEffect(() => {
    let cancelled = false;
    const loadApiKey = async () => {
      try {
        const data = await safeFetch(`${API_URL}/api/settings/yandex_maps_api_key`);
        if (!cancelled) setYandexApiKey(data?.value || '');
      } catch {
        if (!cancelled) setYandexApiKey('');
      }
    };
    loadApiKey();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    forceLegacyTaskMapRef.current = false;
    mapEngineRef.current = 'v3';
    setIsTaskMap3DSupported(true);
  }, [yandexApiKey]);
  const syncCurrentTaskMapViewport = () => {
    if (!mapInstanceRef.current) return;
    if (mapEngineRef.current === 'leaflet') {
      try {
        if (mapRef.current && !mapRef.current.classList.contains('leaflet-container')) {
          mapRef.current.classList.add(
            'leaflet-container',
            'leaflet-touch',
            'leaflet-retina',
            'leaflet-fade-anim',
            'leaflet-grab',
            'leaflet-touch-drag',
            'leaflet-touch-zoom'
          );
        }
        mapInstanceRef.current.invalidateSize?.({ animate: false });
      } catch (err) {
        console.warn('Ошибка syncCurrentTaskMapViewport (Leaflet):', err);
      }
      return;
    }

    if (!mapLocationRef.current.center) return;

    if (mapEngineRef.current === 'v3') {
      try {
        mapInstanceRef.current.update({
          location: {
            center: mapLocationRef.current.center,
            zoom: mapLocationRef.current.zoom,
            azimuth: mapLocationRef.current.azimuth,
            tilt: mapLocationRef.current.tilt,
            duration: 0,
          },
        });
      } catch {}
      return;
    }

    try {
      mapInstanceRef.current.container?.fitToViewport?.();
      mapInstanceRef.current.setCenter?.(
        [mapLocationRef.current.center[1], mapLocationRef.current.center[0]],
        mapLocationRef.current.zoom || TASK_MAP_DEFAULT_ZOOM,
        { duration: 0 },
      );
    } catch {}
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const nextFullscreen = document.fullscreenElement === mapWrapperRef.current;
      setIsTaskMapFullscreen(nextFullscreen);
      window.requestAnimationFrame(() => {
        syncCurrentTaskMapViewport();
      });
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const timers = [
      window.setTimeout(() => syncCurrentTaskMapViewport(), 20),
      window.setTimeout(() => syncCurrentTaskMapViewport(), 100),
      window.setTimeout(() => syncCurrentTaskMapViewport(), 250),
      window.setTimeout(() => syncCurrentTaskMapViewport(), 500)
    ];
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [isTaskMapFullscreen]);

  useEffect(() => {
    if (!mapRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (mapEngineRef.current === 'leaflet' && mapInstanceRef.current) {
        syncCurrentTaskMapViewport();
      }
    });
    ro.observe(mapRef.current);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (!isTaskMapExpanded) {
      setIsResolvingMapCoords(false);
      return undefined;
    }
    if (!task) {
      setResolvedMapCoords(null);
      return;
    }
    const hasStoredCoordinates = Number.isFinite(Number(task.latitude)) && Number.isFinite(Number(task.longitude));
    const hasYandexCoordinates = Number.isFinite(Number(task.yandex_latitude)) && Number.isFinite(Number(task.yandex_longitude));
    if (hasStoredCoordinates) {
      setResolvedMapCoords([Number(task.longitude), Number(task.latitude)]);
      if (hasYandexCoordinates) return;
    }
    if (!task.address_text) {
      setResolvedMapCoords(null);
      return;
    }

    let cancelled = false;
    const resolveCoords = async () => {
      setIsResolvingMapCoords(true);
      try {
        let latitude = null;
        let longitude = null;

        if (yandexApiKey) {
          try {
            const ymaps = await loadYandexMaps(yandexApiKey);
            const result = await ymaps.geocode(task.address_text, { results: 1 });
            const firstGeoObject = result?.geoObjects?.get?.(0);
            const coords = firstGeoObject?.geometry?.getCoordinates?.();
            if (!cancelled && Array.isArray(coords) && coords.length === 2 && coords.every((value) => Number.isFinite(Number(value)))) {
              const lat = Number(coords[0]);
              const lng = Number(coords[1]);
              if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                latitude = lat;
                longitude = lng;
              }
            }
          } catch {}
        }

        if ((latitude === null || longitude === null) && !cancelled) {
          try {
            const osmCoords = await geocodeAddressWithNominatim(task.address_text);
            if (osmCoords) {
              latitude = osmCoords.lat;
              longitude = osmCoords.lng;
            }
          } catch {}
        }

        if (!cancelled && latitude !== null && longitude !== null) {
          setResolvedMapCoords([longitude, latitude]);
          fetch(`${API_URL}/api/tasks/${id}/route-coordinates`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ latitude, longitude })
          })
            .then((response) => {
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              if (!cancelled) {
                setTask((prev) => prev ? {
                  ...prev,
                  latitude,
                  longitude,
                  yandex_latitude: latitude,
                  yandex_longitude: longitude
                } : prev);
              }
            })
            .catch(() => {});
        } else if (!cancelled) {
          setResolvedMapCoords(null);
        }
      } catch {
        if (!cancelled) setResolvedMapCoords(null);
      } finally {
        if (!cancelled) setIsResolvingMapCoords(false);
      }
    };

    resolveCoords();
    return () => { cancelled = true; };
  }, [id, isTaskMapExpanded, task, yandexApiKey]);
  useEffect(() => {
    if (!taskMapHasBeenOpened || !resolvedMapCoords || !mapRef.current) {
      setIsInitializingTaskMap(false);
      return undefined;
    }
    if (mapProvider === 'yandex' && !yandexApiKey) {
      setIsInitializingTaskMap(false);
      return undefined;
    }
    let cancelled = false;
    let stopSuppressingOpenMapsButton = null;
    const initMap = async () => {
      setIsInitializingTaskMap(true);
      try {
        if (mapProvider === 'osm') {
          if (mapInstanceRef.current) {
            if (mapEngineRef.current === 'leaflet') {
              mapInstanceRef.current.remove?.();
            } else {
              mapInstanceRef.current.destroy?.();
            }
            mapInstanceRef.current = null;
          }
          if (mapRef.current) {
            try {
              delete mapRef.current._leaflet_id;
            } catch {}
            mapRef.current.innerHTML = '';
            mapRef.current.className = mapRef.current.className
              .replace(/leaflet-[^\s]+/g, '')
              .trim();
          }
          const lat = resolvedMapCoords[1];
          const lng = resolvedMapCoords[0];
          const map = L.map(mapRef.current, {
            center: [lat, lng],
            zoom: TASK_MAP_DEFAULT_ZOOM,
            zoomControl: false,
            attributionControl: false
          });
          const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
          L.tileLayer(tileUrl, { maxZoom: 19, subdomains: ['a', 'b', 'c'] }).addTo(map);

          const marker = L.marker([lat, lng], {
            icon: L.divIcon({
              className: 'leaflet-div-icon',
              html: `<div style="width:24px;height:24px;border-radius:999px;background:#4f46e5;border:3px solid #ffffff;box-shadow:0 2px 8px rgba(0,0,0,0.3);transform:translate(-50%,-50%);"></div>`,
              iconSize: [0, 0],
              iconAnchor: [0, 0]
            })
          }).addTo(map);

          mapMarkerRef.current = marker;
          map.on('moveend zoomend', () => {
            const c = map.getCenter();
            const z = map.getZoom();
            mapLocationRef.current = {
              center: [c.lng, c.lat],
              zoom: z,
              azimuth: 0,
              tilt: 0
            };
          });

          mapLocationRef.current = {
            center: resolvedMapCoords,
            zoom: TASK_MAP_DEFAULT_ZOOM,
            azimuth: 0,
            tilt: 0
          };
          mapEngineRef.current = 'leaflet';
          setIsTaskMap3DSupported(false);
          setIsTaskMapTilted(false);
          mapInstanceRef.current = map;
          setTimeout(() => {
            if (!cancelled && mapInstanceRef.current && mapEngineRef.current === 'leaflet') {
              mapInstanceRef.current.invalidateSize?.({ animate: false });
            }
          }, 50);
          syncCurrentLocationMarker();
          return;
        }

        if (!forceLegacyTaskMapRef.current) {
          try {
            const { ymaps3 } = await loadYandexMapsV3(yandexApiKey);
            const { YMapDefaultMarker } = await ymaps3.import('@yandex/ymaps3-default-ui-theme');
            if (cancelled || !mapRef.current) return;

            if (mapInstanceRef.current) {
              if (mapEngineRef.current === 'leaflet') {
                mapInstanceRef.current.remove?.();
              } else {
                mapInstanceRef.current.destroy?.();
              }
              mapInstanceRef.current = null;
            }
            if (mapRef.current) {
              mapRef.current.innerHTML = '';
              mapRef.current.className = mapRef.current.className
                .replace(/leaflet-[^\s]+/g, '')
                .trim();
            }

            const location = {
              center: resolvedMapCoords,
              zoom: TASK_MAP_DEFAULT_ZOOM,
            };
            const tilt = isTaskMapTilted ? TASK_MAP_3D_TILT : 0;
            mapLocationRef.current = {
              ...location,
              azimuth: 0,
              tilt,
            };

            const map = new ymaps3.YMap(mapRef.current, {
              location,
              copyrightsPosition: 'bottom right',
              distribution: false,
              distributionPosition: 'bottom right',
              mode: 'vector',
              behaviors: ['drag', 'scrollZoom', 'pinchZoom', 'pinchRotate', 'dblClick', 'magnifier', 'mouseTilt'],
            });
            map.addChild(new ymaps3.YMapDefaultSchemeLayer());
            map.addChild(new ymaps3.YMapDefaultFeaturesLayer());
            const listener = new ymaps3.YMapListener({
              layer: 'any',
              onUpdate: ({ location: nextLocation, camera }) => {
                if (!nextLocation?.center) return;
                mapLocationRef.current = {
                  center: nextLocation.center,
                  zoom: Number.isFinite(nextLocation.zoom) ? nextLocation.zoom : mapLocationRef.current.zoom,
                  azimuth: Number.isFinite(camera?.azimuth) ? camera.azimuth : mapLocationRef.current.azimuth,
                  tilt: Number.isFinite(camera?.tilt) ? camera.tilt : mapLocationRef.current.tilt,
                };
                setIsTaskMapTilted((camera?.tilt || 0) > 0.05);
              },
            });
            map.addChild(listener);
            const marker = new YMapDefaultMarker({ coordinates: resolvedMapCoords });
            map.addChild(marker);

            mapEngineRef.current = 'v3';
            stopSuppressingOpenMapsButton = suppressYandexOpenMapsButton(mapRef.current);
            setIsTaskMap3DSupported(true);
            mapInstanceRef.current = map;
            mapMarkerRef.current = marker;
            syncCurrentLocationMarker();
            return;
          } catch (v3Error) {
            forceLegacyTaskMapRef.current = true;
            console.warn('Яндекс.Карты v3 недоступны, используем fallback на v2:', v3Error);
          }
        }

        const ymaps = await loadYandexMaps(yandexApiKey);
        if (cancelled || !mapRef.current) return;

        if (mapInstanceRef.current) {
          if (mapEngineRef.current === 'leaflet') {
            mapInstanceRef.current.remove?.();
          } else {
            mapInstanceRef.current.destroy?.();
          }
          mapInstanceRef.current = null;
        }
        if (mapRef.current) {
          mapRef.current.innerHTML = '';
          mapRef.current.className = mapRef.current.className
            .replace(/leaflet-[^\s]+/g, '')
            .trim();
        }

        const legacyCoords = [resolvedMapCoords[1], resolvedMapCoords[0]];
        const map = new ymaps.Map(mapRef.current, {
          center: legacyCoords,
          zoom: TASK_MAP_DEFAULT_ZOOM,
          controls: [],
        }, {
          suppressMapOpenBlock: true,
        });
        const marker = new ymaps.Placemark(legacyCoords);
        map.geoObjects.add(marker);
        map.events.add('boundschange', () => {
          const center = map.getCenter?.();
          const zoom = map.getZoom?.();
          if (!center) return;
          mapLocationRef.current = {
            center: [Number(center[1]), Number(center[0])],
            zoom: Number.isFinite(zoom) ? zoom : mapLocationRef.current.zoom,
            azimuth: 0,
            tilt: 0,
          };
        });

        mapLocationRef.current = {
          center: resolvedMapCoords,
          zoom: TASK_MAP_DEFAULT_ZOOM,
          azimuth: 0,
          tilt: 0,
        };
        mapEngineRef.current = 'v2';
        setIsTaskMap3DSupported(false);
        setIsTaskMapTilted(false);
        mapInstanceRef.current = map;
        mapMarkerRef.current = marker;
        syncCurrentLocationMarker();
      } catch (mapError) {
        console.error('Ошибка инициализации карты поручения:', mapError);
      } finally {
        if (!cancelled) setIsInitializingTaskMap(false);
      }
    };

    initMap();
    return () => {
      cancelled = true;
      stopSuppressingOpenMapsButton?.();
      if (mapInstanceRef.current) {
        try {
          if (typeof mapInstanceRef.current.remove === 'function') {
            mapInstanceRef.current.remove();
          } else if (typeof mapInstanceRef.current.destroy === 'function') {
            mapInstanceRef.current.destroy();
          }
        } catch {}
        mapInstanceRef.current = null;
      }
      if (mapRef.current) {
        try {
          delete mapRef.current._leaflet_id;
        } catch {}
      }
      currentLocationMarkerRef.current = null;
    };
  }, [taskMapHasBeenOpened, mapProvider, resolvedMapCoords, syncCurrentLocationMarker, yandexApiKey, isTaskMapTilted]);
  useEffect(() => {
    if (!isTaskMapExpanded || !mapInstanceRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (!mapInstanceRef.current || !mapLocationRef.current.center) return;
      if (mapEngineRef.current === 'v3') {
        mapInstanceRef.current.update({
          location: {
            center: mapLocationRef.current.center,
            zoom: mapLocationRef.current.zoom
          },
          camera: {
            azimuth: mapLocationRef.current.azimuth,
            tilt: mapLocationRef.current.tilt
          }
        });
        return;
      }
      if (mapEngineRef.current === 'leaflet') {
        mapInstanceRef.current.invalidateSize?.();
        return;
      }
      mapInstanceRef.current.container?.fitToViewport?.();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isTaskMapExpanded]);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('taskheadertitle', { detail: { title: task?.task_number ? `Поручение №${task.task_number}` : '' } }));
    return () => window.dispatchEvent(new CustomEvent('taskheadertitle', { detail: { title: '' } }));
  }, [task?.task_number]);
  useEffect(() => {
    if (!photoFiles[0]) {
      setPhotoPreviewUrl('');
      return undefined;
    }

    const nextUrl = URL.createObjectURL(photoFiles[0]);
    setPhotoPreviewUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [photoFiles]);

  const runAction = useCallback(async (action, successMessage) => {
    setActionLoading(true);
    try {
      await action();
      await loadBundle(true);
      if (successMessage) showToast(successMessage, 'success');
    } finally {
      setActionLoading(false);
    }
  }, [loadBundle, showToast]);

  const statusMeta = STATUS_META[task?.status] || STATUS_META.available;
  const StatusIcon = statusMeta.icon;
  const pageAccent = TASK_PAGE_ACCENTS[task?.status] || TASK_PAGE_ACCENTS.available;
  const hasUnreadComments = useMemo(
    () => comments.some((comment) => !comment.is_read && comment.user_id !== currentUserId),
    [comments, currentUserId]
  );

  const mapsUrl = task ? (getYandexMapsUrl(task.address_text || '') || getYandexAddressUrl(task.address_text || '')) : '';
  const navigatorUrl = task ? (getYandexNavigatorUrl(task.address_text || '') || getYandexAddressUrl(task.address_text || '')) : '';
  const dgisUrl = task
    ? (getDgisRouteUrl({
        latitude: task.latitude ?? resolvedMapCoords?.[0],
        longitude: task.longitude ?? resolvedMapCoords?.[1],
        address: task.address_text
      }) || getDgisAddressUrl(task.address_text || ''))
    : '';
  const hasMap = Boolean((yandexApiKey || mapProvider === 'osm') && resolvedMapCoords?.length === 2);
  const hasPhotos = photos.length > 0;
  const canUseDispatcherShield = Boolean(task?.can_edit_dispatch || task?.can_assign || task?.can_cancel);
  const canCompleteWithDispatcherShield = Boolean(
    dispatcherModeEnabled
    && canUseDispatcherShield
    && task?.status === 'assigned'
    && task?.assigned_to
  );
  const canCompleteTask = Boolean(task?.can_complete || canCompleteWithDispatcherShield);
  const canDeferTask = Boolean(
    task?.can_defer
    && (
      task?.assigned_to === currentUserId
      || (dispatcherModeEnabled && canUseDispatcherShield)
    )
  );
  const hasBottomActionBar = !['completed', 'cancelled'].includes(task?.status) && (
    canCompleteTask || task?.can_take || canDeferTask || task?.can_resolve_deferred
  );
  const dispatcherCompletesForAssignee = Boolean(
    canCompleteWithDispatcherShield
    && task?.assigned_to
    && task.assigned_to !== currentUserId
  );
  const dispatcherCompleteCreditLabel = task?.assigned_to_name || (task?.assigned_to ? `#${task.assigned_to}` : 'назначенному сотруднику');
  const requiresPhotoBeforeComplete = Boolean(task?.requires_photo && !hasPhotos);
  const canForceCompleteWithoutPhoto = Boolean(canCompleteWithDispatcherShield);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('orderdispatchermodechange', {
      detail: {
        enabled: Boolean(dispatcherModeEnabled && canUseDispatcherShield)
      }
    }));

    return () => {
      window.dispatchEvent(new CustomEvent('orderdispatchermodechange', {
        detail: { enabled: false }
      }));
    };
  }, [dispatcherModeEnabled, canUseDispatcherShield]);

  const assigneeOptions = useMemo(() => [
    { value: UNASSIGN_TASK_ASSIGNEE_VALUE, label: 'Оставить в доступных', icon: XCircle },
    ...assignees.map((user) => ({
      value: String(user.id),
      label: user.name || `Сотрудник #${user.id}`,
      icon: UserCheck,
    })),
  ], [assignees]);
  const deferredAssigneeOptions = useMemo(() => assignees.map((user) => ({
    value: String(user.id),
    label: user.name || `Сотрудник #${user.id}`,
    icon: UserCheck,
  })), [assignees]);

  const handleComplete = async (forceWithoutPhoto = false) => {
    try {
      haptic('tap');
      await runAction(
        () => completeTask(id, forceWithoutPhoto, { dispatcherAccess: canCompleteWithDispatcherShield }),
        forceWithoutPhoto
          ? 'Поручение закрыто диспетчерским обходом'
          : (dispatcherCompletesForAssignee ? 'Поручение закрыто диспетчерским доступом' : 'Поручение закрыто')
      );
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось закрыть поручение', 'error');
    }
  };

  const handleCompleteButtonClick = () => {
    if (actionLoading) return;
    if (requiresPhotoBeforeComplete) {
      if (canForceCompleteWithoutPhoto) {
        haptic('open');
        setShowCompleteOverrideModal(true);
        return;
      }
      showToast('Для закрытия нужно добавить хотя бы одно фото', 'error');
      return;
    }
    haptic('open');
    setShowCompleteConfirmModal(true);
  };

  const handleCancelFromModal = async () => {
    const reason = String(cancelReasonDraft || '').trim();
    if (reason.length < 3) {
      showToast('Укажите причину отмены', 'error');
      return;
    }
    try {
      haptic('tap');
      await runAction(() => cancelTask(id, reason), 'Поручение отменено');
      setShowCancelModal(false);
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось отменить поручение', 'error');
    }
  };

  const handleDeferFromModal = async () => {
    const reasonCode = String(deferDraft.reason_code || '').trim();
    const comment = String(deferDraft.comment || '').trim();
    if (!reasonCode) {
      showToast('Выберите причину', 'error');
      return;
    }
    if (comment.length < 3) {
      showToast('Добавьте короткий комментарий', 'error');
      return;
    }
    setActionLoading(true);
    try {
      haptic('tap');
      await deferTask(id, { reason_code: reasonCode, comment }, {
        dispatcherAccess: dispatcherModeEnabled && task?.assigned_to !== currentUserId,
      });
      setShowDeferModal(false);
      navigate(backRoute, { replace: true });
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось отложить поручение', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeferredResolveModal = () => {
    setDeferredResolveDraft({
      resolution: 'available',
      scheduled_date: '',
      time_slot: '',
      assigned_to: '',
    });
    setShowDeferredResolveModal(true);
  };

  const handleResolveDeferred = async () => {
    const payload = {
      resolution: deferredResolveDraft.resolution,
      scheduled_date: deferredResolveDraft.scheduled_date,
      time_slot: deferredResolveDraft.time_slot,
      assigned_to: deferredResolveDraft.resolution === 'assigned'
        ? Number(deferredResolveDraft.assigned_to || 0) || null
        : null,
    };
    if (!payload.scheduled_date || !String(payload.time_slot || '').trim()) {
      showToast('Укажите новые дату и время', 'error');
      return;
    }
    if (payload.resolution === 'assigned' && !payload.assigned_to) {
      showToast('Выберите исполнителя', 'error');
      return;
    }
    try {
      haptic('tap');
      await runAction(() => resolveDeferredTask(id, payload), 'Поручение возвращено в работу');
      setShowDeferredResolveModal(false);
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось вернуть поручение в работу', 'error');
    }
  };

  const handleComment = async () => {
    const text = String(commentText || '').trim();
    if (!text) {
      showToast('Введите комментарий', 'error');
      return;
    }
    try {
      haptic('tap');
      setActionLoading(true);
      await addTaskComment(id, text);
      setCommentText('');
      setShowCommentModal(false);
      await loadBundle(true);
      showToast('Комментарий добавлен', 'success');
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось добавить комментарий', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePhotoUpload = async () => {
    if (uploadingPhotoRef.current || isUploadingPhoto || actionLoading) return;
    if (!photoFiles.length) {
      showToast('Выберите хотя бы одно фото', 'error');
      return;
    }
    uploadingPhotoRef.current = true;
    setIsUploadingPhoto(true);
    setActionLoading(true);
    setUploadProgress(0);

    const firstFile = photoFiles[0];
    const tempPreviewUrl = firstFile ? URL.createObjectURL(firstFile) : '';
    if (tempPreviewUrl) {
      setUploadingPlaceholder({
        previewUrl: tempPreviewUrl,
        caption: photoCaption?.trim() || firstFile.name,
        progress: 0,
      });
    }

    try {
      haptic('tap');
      for (const file of photoFiles) {
        let preparedPhoto = file;
        if (file.size > 11.5 * 1024 * 1024) {
          preparedPhoto = await compressImageFile(file);
        }
        await uploadTaskPhoto(id, preparedPhoto, photoCaption, (percent) => {
          setUploadProgress(percent);
          setUploadingPlaceholder((prev) => (prev ? { ...prev, progress: percent } : null));
        });
      }
      setPhotoCaption('');
      setPhotoFiles([]);
      setShowPhotoModal(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadBundle(true);
      showToast('Фото загружены', 'success');
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось загрузить фото', 'error');
    } finally {
      uploadingPhotoRef.current = false;
      setIsUploadingPhoto(false);
      setActionLoading(false);
      setUploadProgress(null);
      setUploadingPlaceholder(null);
      if (tempPreviewUrl) URL.revokeObjectURL(tempPreviewUrl);
    }
  };

  const handleSaveScheduleModal = async () => {
    try {
      haptic('tap');
      await runAction(() => updateTask(id, {
        scheduled_date: dispatchForm.scheduled_date,
        time_slot: dispatchForm.time_slot || null,
      }), 'Дата и время обновлены');
      setShowScheduleModal(false);
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось сохранить дату и время', 'error');
    }
  };

  const handleSaveAddressModal = async () => {
    const typedAddress = String(dispatchForm.address_text || '').trim();
    const originalAddress = String(task?.address_text || '').trim();
    const selectedAddressText = getSuggestionAddressText(selectedAddress);
    const isOriginalAddress = typedAddress && typedAddress === originalAddress;

    if (!isOriginalAddress && (!selectedAddress || typedAddress !== selectedAddressText)) {
      showToast('Выберите адрес из подсказок', 'error');
      return;
    }
    if (!isOriginalAddress && !isCompleteServiceAddress(selectedAddress)) {
      showToast(incompleteServiceAddressMessage(), 'error');
      return;
    }

    try {
      const coordinateData = isOriginalAddress ? task : (selectedAddress?.data || {});
      const coordinates = await resolvePreferredAddressCoordinates({
        apiKey: yandexApiKey,
        addressText: typedAddress,
        data: coordinateData
      });
      haptic('tap');
      await runAction(() => updateTask(id, {
        address_text: typedAddress,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        dadata_latitude: coordinates.dadataLatitude,
        dadata_longitude: coordinates.dadataLongitude,
        yandex_latitude: coordinates.yandexLatitude,
        yandex_longitude: coordinates.yandexLongitude,
      }), 'Адрес обновлён');
      setShowAddressModal(false);
      setShowAddressSuggestions(false);
      setSelectedAddress(null);
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось сохранить адрес', 'error');
    }
  };

  const handleSaveInfoModal = async () => {
    try {
      haptic('tap');
      await runAction(() => updateTask(id, {
        description: dispatchForm.description,
        requires_photo: dispatchForm.requires_photo,
      }), 'Информация обновлена');
      setShowInfoModal(false);
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось сохранить информацию', 'error');
    }
  };

  const handleSaveAssigneeModal = async () => {
    try {
      haptic('tap');
      const nextAssigneeId = assigneeDraft && assigneeDraft !== UNASSIGN_TASK_ASSIGNEE_VALUE ? Number(assigneeDraft) : null;
      await runAction(() => assignTask(id, nextAssigneeId), nextAssigneeId ? 'Исполнитель обновлён' : 'Поручение возвращено в доступные');
      setShowAssigneeModal(false);
    } catch (requestError) {
      showToast(requestError.message || 'Не удалось обновить исполнителя', 'error');
    }
  };

  const fetchAddressSuggestions = useCallback(async (query) => {
    const trimmed = String(query || '').trim();
    const requestId = addressRequestIdRef.current + 1;
    addressRequestIdRef.current = requestId;

    if (trimmed.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/dadata/address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ query: trimmed, count: 6 }),
      });

      const data = await response.json().catch(() => ([]));
      if (requestId !== addressRequestIdRef.current) return;

      const suggestions = Array.isArray(data) ? data : (data.suggestions || []);
      setAddressSuggestions(suggestions);
      setShowAddressSuggestions(suggestions.length > 0);
    } catch {
      if (requestId !== addressRequestIdRef.current) return;
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  }, []);

  const handleAddressChange = useCallback((value) => {
    addressCoordinateRequestRef.current += 1;
    setDispatchForm((prev) => ({ ...prev, address_text: value }));
    setSelectedAddress(null);
    fetchAddressSuggestions(value);
  }, [fetchAddressSuggestions]);

  const applyAddressSuggestion = useCallback((suggestion) => {
    haptic('tap');
    const nextAddress = getSuggestionAddressText(suggestion);
    const requestId = ++addressCoordinateRequestRef.current;
    setDispatchForm((prev) => ({ ...prev, address_text: nextAddress }));
    setSelectedAddress(suggestion);
    resolvePreferredAddressCoordinates({ apiKey: yandexApiKey, addressText: nextAddress, data: suggestion?.data || {} })
      .then((coordinates) => {
        if (requestId !== addressCoordinateRequestRef.current) return;
        setSelectedAddress(mergeAddressSuggestionCoordinates(suggestion, coordinates));
      });
    setShowAddressSuggestions(false);
  }, [yandexApiKey]);

  const selectAddressSuggestion = useCallback((suggestion) => {
    const validation = getServiceAddressValidation(suggestion);
    if (validation.isComplete && !validation.hasStreet) {
      setAddressWithoutStreetSuggestion(suggestion);
      return;
    }
    applyAddressSuggestion(suggestion);
  }, [applyAddressSuggestion]);

  const toggleSection = useCallback((key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const centerTaskMapOnPoint = useCallback(() => {
    if (!mapInstanceRef.current || !mapLocationRef.current.center) return;
    haptic('selection');
    if (mapEngineRef.current === 'leaflet') {
      mapInstanceRef.current.setView(
        [mapLocationRef.current.center[1], mapLocationRef.current.center[0]],
        mapLocationRef.current.zoom || TASK_MAP_DEFAULT_ZOOM,
        { animate: true }
      );
      return;
    }
    if (mapEngineRef.current === 'v3') {
      mapInstanceRef.current.update({
        location: {
          center: mapLocationRef.current.center,
          zoom: mapLocationRef.current.zoom,
          azimuth: mapLocationRef.current.azimuth,
          tilt: mapLocationRef.current.tilt,
          duration: 250,
        },
      });
      return;
    }

    mapInstanceRef.current.setCenter?.(
      [mapLocationRef.current.center[1], mapLocationRef.current.center[0]],
      mapLocationRef.current.zoom || TASK_MAP_DEFAULT_ZOOM,
      { duration: 250 },
    );
  }, []);

  const centerTaskMapOnCurrentLocation = useCallback((coordinates) => {
    if (!mapInstanceRef.current) return;
    currentLocationCoordsRef.current = coordinates;
    syncCurrentLocationMarker(coordinates);
    if (mapEngineRef.current === 'leaflet') {
      mapInstanceRef.current.setView(
        [coordinates[1], coordinates[0]],
        CURRENT_LOCATION_MAP_ZOOM,
        { animate: true }
      );
    } else if (mapEngineRef.current === 'v3') {
      mapInstanceRef.current.setLocation({
        center: coordinates,
        zoom: CURRENT_LOCATION_MAP_ZOOM,
        duration: 250,
      });
    } else {
      mapInstanceRef.current.setCenter?.(
        [coordinates[1], coordinates[0]],
        CURRENT_LOCATION_MAP_ZOOM,
        { duration: 250 },
      );
    }
    mapLocationRef.current = {
      ...mapLocationRef.current,
      center: coordinates,
      zoom: CURRENT_LOCATION_MAP_ZOOM,
    };
  }, [syncCurrentLocationMarker]);

  const showTaskMapLocationError = useCallback((message) => {
    showToast(message, 'error');
  }, [showToast]);

  const toggleTaskMapFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement === mapWrapperRef.current) {
        haptic('close');
        await document.exitFullscreen?.();
      } else {
        haptic('open');
        await mapWrapperRef.current?.requestFullscreen?.();
      }
    } catch (mapError) {
      console.error('Ошибка полноэкранного режима карты поручения:', mapError);
    }
  }, []);

  const toggleTaskMapDisclosure = useCallback(() => {
    const nextExpanded = !isTaskMapExpanded;
    haptic(nextExpanded ? 'open' : 'close');
    if (nextExpanded) setTaskMapEverOpenedKey(taskMapDisclosureKey);
    setTaskMapDisclosure({ key: taskMapDisclosureKey, expanded: nextExpanded });
  }, [isTaskMapExpanded, taskMapDisclosureKey]);

  const toggleTaskMapDimension = useCallback(() => {
    if (!isTaskMap3DSupported || !mapInstanceRef.current || !mapLocationRef.current.center) return;
    const nextTilted = !isTaskMapTilted;
    setIsTaskMapTilted(nextTilted);
    mapLocationRef.current = {
      ...mapLocationRef.current,
      azimuth: 0,
      tilt: nextTilted ? TASK_MAP_3D_TILT : 0,
    };
    mapInstanceRef.current.update({
      location: {
        center: mapLocationRef.current.center,
        zoom: mapLocationRef.current.zoom,
        azimuth: mapLocationRef.current.azimuth,
        tilt: mapLocationRef.current.tilt,
        duration: 250,
      },
    });
  }, [isTaskMap3DSupported, isTaskMapTilted]);

  const handleDownloadSelectedPhoto = useCallback(async () => {
    if (!selectedPhoto) return;
    const photoUrl = getTaskPhotoUrl(selectedPhoto, 'file');
    if (!photoUrl) return;
    const extension = String(selectedPhoto.file_path || '').split('.').pop() || 'jpg';
    const baseName = [
      'task_photo',
      selectedPhoto.id || 'image',
    ].filter(Boolean).join('_');
    try {
      const blob = await fetchProtectedBlob(photoUrl);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${baseName}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      haptic('success');
    } catch (error) {
      console.error('Ошибка скачивания фото поручения:', error);
      haptic('error');
      showToast('Не удалось скачать фото', 'error');
    }
  }, [selectedPhoto, showToast]);

  const handleDeleteTaskPhoto = useCallback(async (photo) => {
    if (!photo || !id) return;
    setActionLoading(true);
    try {
      await deleteTaskPhoto(id, photo.id);
      setSelectedPhoto(null);
      setPhotoToDelete(null);
      showToast('Фото удалено с сервера безвозвратно', 'success');
      await loadBundle(true);
    } catch (err) {
      showToast(err?.message || 'Не удалось удалить фото', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [id, loadBundle, showToast]);

  useEffect(() => {
    const markVisibleCommentsRead = async () => {
      if (!id || !task || !expandedSections.comments || task.assigned_to !== currentUserId) return;
      const unreadComments = comments.filter((comment) => !comment.is_read && comment.user_id !== currentUserId);
      if (!unreadComments.length) return;
      setComments((prev) => prev.map((comment) => (
        unreadComments.some((item) => item.id === comment.id) ? { ...comment, is_read: true } : comment
      )));
      try {
        await Promise.all(unreadComments.map((comment) => markTaskCommentRead(id, comment.id)));
      } catch {}
    };

    markVisibleCommentsRead();
  }, [comments, currentUserId, expandedSections.comments, id, task]);

  if (loading) {
    return <TaskDetailSkeleton />;
  }

  if (error || !task) {
    return (
      <div className="space-y-4">
        <div className="rounded-3xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">{error || 'Поручение не найдено'}</div>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={() => navigate(backRoute)} className="rounded-2xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">Назад к списку</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col gap-4 desktop-content-container-wide">
      <section className={`-mx-4 -mt-4 overflow-hidden rounded-b-lg border-b bg-white shadow-sm dark:bg-gray-900 ${pageAccent.border}`}>
        {!isTaskMapExpanded && !isTaskMapFullscreen ? (
          <button
            type="button"
            onClick={toggleTaskMapDisclosure}
            aria-expanded={isTaskMapExpanded}
            aria-controls="task-address-map-panel"
            className="group flex w-full items-center justify-between gap-3 bg-gradient-to-r from-white via-white to-gray-50/80 px-4 py-3 text-left transition-colors hover:from-gray-50 hover:to-gray-100/80 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800/70 dark:hover:from-gray-800 dark:hover:to-gray-800"
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${pageAccent.border} ${pageAccent.text} bg-white/70 dark:bg-gray-900/70`}>
                <MapPin className="h-4 w-4" />
              </span>
              <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">Карта поручения</span>
            </span>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/80 text-gray-500 shadow-sm transition-colors group-hover:text-gray-700 dark:border-gray-700 dark:bg-gray-900/80 dark:text-gray-400 dark:group-hover:text-gray-200">
              <ChevronDown className="h-4 w-4" />
            </span>
          </button>
        ) : null}
        {(isTaskMapExpanded || taskMapHasBeenOpened) ? (hasMap ? (
          <div ref={mapWrapperRef} className={`relative ${!isTaskMapExpanded ? 'hidden' : ''} ${isTaskMapFullscreen ? 'h-screen bg-gray-100 p-3 dark:bg-slate-900' : ''}`}>
            <div
              className={`relative isolate z-0 w-full overflow-hidden bg-gray-100 ${isTaskMapFullscreen ? 'h-[calc(100dvh-24px)] rounded-2xl' : 'h-[210px] dark:bg-gray-800 sm:h-[240px]'}`}
            >
              <div
                id="task-address-map-panel"
                ref={mapRef}
                className="order-detail-map h-full w-full"
              />
            </div>
            {isInitializingTaskMap ? (
              <MapLoadingSkeleton className={isTaskMapFullscreen ? 'rounded-2xl' : ''} />
            ) : null}
            {isTaskMapFullscreen ? (
              <>
                <div className="pointer-events-none absolute left-3 top-3 sm:left-5 sm:top-5 z-20 flex items-center gap-2">
                  <div className="pointer-events-auto">
                    <MapProviderToggle
                      className="h-11 w-11"
                      provider={mapProvider}
                      onChange={(nextProvider) => {
                        if (mapInstanceRef.current) {
                          if (mapEngineRef.current === 'leaflet') {
                            mapInstanceRef.current.remove?.();
                          } else {
                            mapInstanceRef.current.destroy?.();
                          }
                          mapInstanceRef.current = null;
                        }
                        if (mapRef.current) {
                          mapRef.current.innerHTML = '';
                          mapRef.current.className = mapRef.current.className
                            .replace(/leaflet-[^\s]+/g, '')
                            .trim();
                        }
                        setMapProvider(nextProvider);
                      }}
                    />
                  </div>
                </div>

                <div className="pointer-events-none absolute left-3 bottom-3 sm:left-5 sm:bottom-5 z-20 flex items-center gap-2">
                  <div className="pointer-events-auto flex items-center gap-2">
                    {mapsUrl ? <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900" title="Открыть в Яндекс.Картах" aria-label="Открыть в Яндекс.Картах"><img src={yaMapsIcon} alt="" className="h-5 w-5 object-contain" /></a> : null}
                    {navigatorUrl ? <a href={navigatorUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900" title="Открыть в Яндекс.Навигаторе" aria-label="Открыть в Яндекс.Навигаторе"><img src={yandexNavigatorIcon} alt="" className="h-5 w-5 object-contain" /></a> : null}
                    {dgisUrl ? <a href={dgisUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900" title="Открыть в 2ГИС" aria-label="Открыть в 2ГИС"><img src={dgisIcon} alt="2ГИС" className="h-5 w-5 object-contain" /></a> : null}
                  </div>
                </div>

                <div className="pointer-events-none absolute right-3 top-3 sm:right-5 sm:top-5 z-20 flex flex-col items-end gap-2">
                  <div className="pointer-events-auto flex flex-col items-end gap-2">
                    <button type="button" onClick={toggleTaskMapFullscreen} className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"><Minimize2 className="h-4 w-4" /></button>
                    <button type="button" onClick={centerTaskMapOnPoint} className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900" title="Центрировать на поручении" aria-label="Центрировать на поручении"><LocateFixed className="h-4 w-4" /></button>
                    <MapCurrentLocationButton
                      onLocated={centerTaskMapOnCurrentLocation}
                      onError={showTaskMapLocationError}
                    />
                    <button type="button" onClick={toggleTaskMapDimension} disabled={!isTaskMap3DSupported} className={`inline-flex h-10 min-w-[48px] items-center justify-center rounded-2xl border border-gray-200 bg-white/95 px-2.5 text-xs font-semibold text-gray-700 shadow-sm backdrop-blur transition-colors dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 ${isTaskMap3DSupported ? 'hover:bg-white dark:hover:bg-gray-900' : 'cursor-not-allowed opacity-50'}`} title={isTaskMap3DSupported ? (isTaskMapTilted ? 'Переключить в 2D' : 'Переключить в 3D') : '3D недоступен для текущего ключа Яндекс.Карт'} aria-label={isTaskMap3DSupported ? (isTaskMapTilted ? 'Переключить в 2D' : 'Переключить в 3D') : '3D недоступен для текущего ключа Яндекс.Карт'}>{isTaskMapTilted ? '2D' : '3D'}</button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="pointer-events-none absolute left-2.5 bottom-2.5 z-20 flex items-center gap-2">
                  <div className="pointer-events-auto flex items-center gap-2">
                    {mapsUrl ? <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900"><img src={yaMapsIcon} alt="" className="h-5 w-5 object-contain" /></a> : null}
                    {navigatorUrl ? <a href={navigatorUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900"><img src={yandexNavigatorIcon} alt="" className="h-5 w-5 object-contain" /></a> : null}
                    {dgisUrl ? <a href={dgisUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900" title="Открыть в 2ГИС" aria-label="Открыть в 2ГИС"><img src={dgisIcon} alt="2ГИС" className="h-5 w-5 object-contain" /></a> : null}
                  </div>
                </div>
                <div className="pointer-events-none absolute left-2.5 top-2.5 z-20 flex items-center gap-2">
                  <div className="pointer-events-auto">
                    <MapProviderToggle
                      provider={mapProvider}
                      onChange={(nextProvider) => {
                        if (mapInstanceRef.current) {
                          if (mapEngineRef.current === 'leaflet') {
                            mapInstanceRef.current.remove?.();
                          } else {
                            mapInstanceRef.current.destroy?.();
                          }
                          mapInstanceRef.current = null;
                        }
                        if (mapRef.current) {
                          mapRef.current.innerHTML = '';
                          mapRef.current.className = mapRef.current.className
                            .replace(/leaflet-[^\s]+/g, '')
                            .trim();
                        }
                        setMapProvider(nextProvider);
                      }}
                    />
                  </div>
                </div>
                <div className="pointer-events-none absolute right-2.5 top-2.5 z-20 flex flex-col gap-2">
                  <button type="button" onClick={toggleTaskMapFullscreen} className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"><Maximize2 className="h-4 w-4" /></button>
                  <button type="button" onClick={centerTaskMapOnPoint} className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900" title="Центрировать на поручении" aria-label="Центрировать на поручении"><LocateFixed className="h-4 w-4" /></button>
                  <MapCurrentLocationButton
                    onLocated={centerTaskMapOnCurrentLocation}
                    onError={showTaskMapLocationError}
                  />
                </div>
                <button
                  type="button"
                  onClick={toggleTaskMapDisclosure}
                  className="pointer-events-auto cursor-pointer touch-manipulation absolute bottom-0 left-1/2 z-30 flex h-6 min-w-[44px] -translate-x-1/2 translate-y-[17%] items-center justify-center rounded-full border border-gray-200 bg-white/90 px-3 text-gray-600 shadow-sm backdrop-blur transition-colors hover:bg-white hover:text-gray-800 dark:border-gray-700 dark:bg-gray-900/90 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                  title="Свернуть карту"
                  aria-label="Свернуть карту"
                >
                  <ChevronDown className="h-4 w-4 rotate-180 pointer-events-none" />
                </button>
              </>
            )}
          </div>
        ) : (
          <div id="task-address-map-panel" className={`relative flex h-[190px] flex-col items-center justify-center bg-gray-100 px-4 text-center dark:bg-gray-800 sm:h-[220px] ${!isTaskMapExpanded ? 'hidden' : ''}`}>
            {isResolvingMapCoords || refreshing ? (
              <MapLoadingSkeleton />
            ) : (
              <>
                <MapPin className="mb-3 h-10 w-10 text-gray-400" />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{yandexApiKey ? 'Нет координат для карты' : 'Ключ Яндекс.Карт не настроен'}</p>
              </>
            )}
            <button
              type="button"
              onClick={toggleTaskMapDisclosure}
              className="pointer-events-auto cursor-pointer touch-manipulation absolute bottom-0 left-1/2 z-30 flex h-6 min-w-[44px] -translate-x-1/2 translate-y-[17%] items-center justify-center rounded-full border border-gray-200 bg-white/90 px-3 text-gray-600 shadow-sm backdrop-blur transition-colors hover:bg-white hover:text-gray-800 dark:border-gray-700 dark:bg-gray-900/90 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
              title="Свернуть карту"
              aria-label="Свернуть карту"
            >
              <ChevronDown className="h-4 w-4 rotate-180 pointer-events-none" />
            </button>
          </div>
        )) : null}

        <div className={`grid border-t text-sm lg:grid-cols-2 ${pageAccent.border}`}>
          <div className={`px-4 py-2.5 lg:border-r ${pageAccent.border}`}>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex max-w-full items-center gap-1.5 truncate rounded-full border px-2 py-1 text-[11px] font-semibold ${statusMeta.className}`}>
                  <StatusIcon className="h-3.5 w-3.5 shrink-0" />
                  {task.status_label || statusMeta.label}
                </span>
                <span className={`inline-flex max-w-full items-center gap-1.5 truncate rounded-full border px-2 py-1 text-[11px] font-semibold ${pageAccent.border} ${pageAccent.text} bg-white dark:bg-gray-900`}>
                  <ListTodo className="h-3.5 w-3.5 shrink-0" />
                  Поручение
                </span>
              </div>

              <div className="grid grid-cols-[minmax(0,1fr)_minmax(132px,auto)] items-start gap-x-3 gap-y-1.5">
                <div className="space-y-1.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <UserCheck className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className={`min-w-0 truncate text-[11px] font-normal ${pageAccent.text}`}>{task.assigned_to_name || 'Не назначен'}</span>
                  </div>
                </div>

                <div className="space-y-1 min-w-[132px] text-right">
                  <div className="flex min-w-0 items-center justify-end gap-2">
                    <CalendarDays className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="min-w-0 truncate text-[13px] font-normal text-gray-700 dark:text-gray-200">{formatDateValue(task.scheduled_date_key)}</span>
                  </div>
                  <div className="flex min-w-0 items-center justify-end gap-2">
                    <Clock3 className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="min-w-0 truncate text-[11px] font-normal text-gray-700 dark:text-gray-200">{task.time_slot || 'Время не указано'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-100 px-4 py-2.5 dark:border-gray-800 lg:border-t-0">
            <div className="grid gap-y-1.5">
              <div className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-x-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <span className="min-w-0 text-[13px] font-normal leading-[1.25] text-gray-700 dark:text-gray-200">{task.address_text || 'Адрес не указан'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {task.status === 'deferred' && task.deferred_event ? (
        <section className="-mx-4 border-y border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/70 dark:bg-amber-950/25">
          <div className="flex items-start gap-3">
            <PauseCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-300" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-950 dark:text-amber-100">
                {task.deferred_event.reason_label || 'Поручение отложено'}
              </div>
              <div className="mt-1 whitespace-pre-wrap break-words text-sm leading-5 text-amber-900 dark:text-amber-200">
                {task.deferred_event.comment}
              </div>
              <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                Отложено {formatTimezoneDate(task.deferred_event.deferred_at, true) || '—'}
                {task.deferred_event.previous_assignee_name ? ` · было у: ${task.deferred_event.previous_assignee_name}` : ''}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="-mx-4 overflow-hidden border-y border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            <Info className="h-4 w-4 text-indigo-500" />
            Основная информация
          </div>
        </div>
        <div className="px-4 py-3">
          <div className="whitespace-pre-wrap break-words text-[13px] leading-[1.35] text-gray-700 dark:text-gray-200">
            {task.description || 'Описание не заполнено'}
          </div>
        </div>
      </section>

      <div className="space-y-1">
        {(task.can_edit_dispatch || task.can_assign || task.can_cancel) ? (
          <AccordionSection title="Диспетчерская" icon={Shield} className="order-0" open={expandedSections.dispatcher} onToggle={() => toggleSection('dispatcher')}>
            <div className="flex flex-wrap gap-2">
              {canUseDispatcherShield && task.status !== 'deferred' ? (
                <button type="button" onClick={() => { haptic(dispatcherModeEnabled ? 'close' : 'open'); setDispatcherModeEnabled((prev) => !prev); }} disabled={actionLoading} title={dispatcherModeEnabled ? 'Выключить диспетчерский доступ' : 'Включить диспетчерский доступ'} aria-label={dispatcherModeEnabled ? 'Выключить диспетчерский доступ' : 'Включить диспетчерский доступ'} className={`${DISPATCHER_ICON_BUTTON_BASE} ${dispatcherModeEnabled ? 'border-violet-300 bg-violet-100 text-violet-700 hover:bg-violet-200 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-950/60' : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                  <Shield className="h-4.5 w-4.5" />
                </button>
              ) : null}
              {task.can_edit_dispatch ? (
                <button
                  type="button"
                  onClick={() => {
                    if (task.status === 'deferred') {
                      showToast('Измените дату и время через «Вернуть в работу»', 'info');
                      return;
                    }
                    haptic('open');
                    setShowScheduleModal(true);
                  }}
                  disabled={actionLoading}
                  title={task.status === 'deferred' ? 'Доступно через «Вернуть в работу»' : 'Изменить дату и время'}
                  aria-label="Изменить дату и время"
                  className={`${DISPATCHER_ICON_BUTTON_BASE} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50 ${task.status === 'deferred' ? 'opacity-45 grayscale' : ''}`}
                >
                  <Calendar className="h-4.5 w-4.5" />
                </button>
              ) : null}
              {task.can_edit_dispatch ? (
                <button type="button" onClick={() => { haptic('open'); setSelectedAddress(null); setShowAddressSuggestions(false); setShowAddressModal(true); }} disabled={actionLoading} title="Сменить адрес" aria-label="Сменить адрес" className={`${DISPATCHER_ICON_BUTTON_BASE} border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-300 dark:hover:bg-sky-950/50`}>
                  <MapPin className="h-4.5 w-4.5" />
                </button>
              ) : null}
              {task.can_edit_dispatch ? (
                <button type="button" onClick={() => { haptic('open'); setShowInfoModal(true); }} disabled={actionLoading} title="Основная информация" aria-label="Основная информация" className={`${DISPATCHER_ICON_BUTTON_BASE} border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-indigo-300 dark:hover:bg-indigo-950/50`}>
                  <Info className="h-4.5 w-4.5" />
                </button>
              ) : null}
              {task.can_assign || (task.status === 'deferred' && task.can_edit_dispatch) ? (
                <button
                  type="button"
                  onClick={() => {
                    if (task.status === 'deferred') {
                      showToast('Назначьте исполнителя через «Вернуть в работу»', 'info');
                      return;
                    }
                    haptic('open');
                    setShowAssigneeModal(true);
                  }}
                  disabled={actionLoading}
                  title={task.status === 'deferred' ? 'Доступно через «Вернуть в работу»' : 'Назначить исполнителя'}
                  aria-label="Назначить исполнителя"
                  className={`${DISPATCHER_ICON_BUTTON_BASE} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50 ${task.status === 'deferred' ? 'opacity-45 grayscale' : ''}`}
                >
                  <UserCog className="h-4.5 w-4.5" />
                </button>
              ) : null}
              {task.can_cancel && task.status !== 'deferred' ? (
                <button type="button" onClick={() => { haptic('warning'); setShowCancelModal(true); }} disabled={actionLoading} title="Отменить поручение" aria-label="Отменить поручение" className={`${DISPATCHER_ICON_BUTTON_BASE} border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/50`}>
                  <XCircle className="h-4.5 w-4.5" />
                </button>
              ) : null}
            </div>
          </AccordionSection>
        ) : null}
        <AccordionSection title="Комментарии" icon={MessageSquare} unread={hasUnreadComments} className="order-1" contentClassName="p-0" open={expandedSections.comments} onToggle={() => toggleSection('comments')}>
          <div className="max-h-[320px] overflow-y-auto p-2">
            <div className="mb-2 flex justify-end">
              {task.can_comment ? (
                <button type="button" onClick={() => { haptic('open'); setCommentText(''); setShowCommentModal(true); }} className="flex h-8 items-center gap-1 rounded-lg border-2 border-blue-500 px-2.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/30">
                  Добавить
                </button>
              ) : null}
            </div>
            {comments.length === 0 ? (
              <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">Комментариев пока нет</p>
            ) : (
              <div className="space-y-1.5 text-sm">
                {comments.map((comment) => (
                  <div key={comment.id} className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/70">
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">{comment.user_name || `#${comment.user_id}`}</span>
                      <span className="shrink-0 text-[11px] text-gray-500">{formatTimezoneDate(comment.created_date, true)}</span>
                    </div>
                    <p className="mt-1 break-words text-xs leading-4 text-gray-700 dark:text-gray-300">{comment.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </AccordionSection>

        <AccordionSection title="Фото" icon={FileImage} className="order-2" contentClassName="p-0" titleClassName={task.requires_photo ? (hasPhotos ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-red-300') : ''} iconClassName={task.requires_photo ? (hasPhotos ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-red-300') : ''} open={expandedSections.photos} onToggle={() => toggleSection('photos')}>
          <div className="max-h-[340px] overflow-y-auto p-2">
            <div className="mb-2 flex justify-end">
              {task.can_upload_photos ? (
                <button type="button" onClick={() => { haptic('open'); setPhotoCaption(''); setPhotoFiles([]); if (fileInputRef.current) fileInputRef.current.value = ''; setShowPhotoModal(true); }} className="flex h-8 items-center gap-1 rounded-lg border-2 border-blue-500 px-2.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/30">
                  Добавить
                </button>
              ) : null}
            </div>
            {photos.length === 0 && !uploadingPlaceholder ? (
              <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">Фото пока нет</p>
            ) : (
              <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10">
                {uploadingPlaceholder && (
                  <div className="group relative overflow-hidden rounded-lg bg-gray-100 text-left ring-2 ring-blue-500 dark:bg-gray-800">
                    <div className="relative aspect-square bg-gray-900">
                      <img
                        src={uploadingPlaceholder.previewUrl}
                        alt="Загрузка"
                        className="h-full w-full object-cover filter blur-[1px] brightness-50"
                      />
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
                        <CircularProgress
                          progress={uploadingPlaceholder.progress}
                          size={36}
                          strokeWidth={3}
                          indicatorColor="text-white"
                          trackColor="text-white/20"
                          showPercent={true}
                        />
                      </div>
                    </div>
                    <div className="space-y-0.5 px-2 py-1.5">
                      <div className="truncate text-[11px] font-semibold text-blue-600 dark:text-blue-400">
                        {typeof uploadingPlaceholder.progress === 'number' && uploadingPlaceholder.progress < 100
                          ? `Загрузка ${uploadingPlaceholder.progress}%`
                          : 'Сохранение...'}
                      </div>
                      <div className="truncate text-[10px] text-gray-500 dark:text-gray-400">
                        {uploadingPlaceholder.caption || 'Фото'}
                      </div>
                    </div>
                  </div>
                )}
                {photos.map((photo) => (
                  <button key={photo.id} type="button" onClick={() => { haptic('open'); setSelectedPhoto(photo); }} className="group overflow-hidden rounded-lg bg-gray-100 text-left ring-1 ring-gray-200 transition-all hover:ring-2 hover:ring-blue-500/50 dark:bg-gray-800 dark:ring-gray-700">
                    <div className="aspect-square bg-gray-200 dark:bg-gray-800">
                      <ProtectedImage src={getTaskPhotoUrl(photo, 'thumbnail')} alt={photo.caption || photo.original_filename || 'Фото поручения'} className="h-full w-full object-cover" loading="lazy" />
                    </div>
                    <div className="space-y-0.5 px-2 py-1.5">
                      <div className="truncate text-[11px] font-medium text-gray-900 dark:text-gray-100">
                        {photo.id ? `Фото #${photo.id}` : 'К поручению'}
                      </div>
                      <div className="truncate text-[10px] text-gray-500 dark:text-gray-400">
                        {photo.caption || photo.original_filename || photo.uploader_name || 'Без подписи'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </AccordionSection>

        <AccordionSection title="История" icon={History} className="order-3" contentClassName="p-0" open={expandedSections.history} onToggle={() => toggleSection('history')}>
          <div className="max-h-[320px] overflow-y-auto p-2">
            {history.length === 0 ? (
              <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">История пока пуста</p>
            ) : (
              <div className="space-y-1.5 text-sm">
                {history.map((entry) => {
                  const details = formatPayload(entry.new_value) || formatPayload(entry.old_value);
                  return (
                    <div key={entry.id} className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/70">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex items-center gap-2">
                          <span className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">{ACTION_LABELS[entry.action] || entry.action}</span>
                        </div>
                        <span className="shrink-0 text-[11px] text-gray-500">{formatTimezoneDate(entry.created_date, true)}</span>
                      </div>
                      {details ? <div className="mt-1 break-words text-xs leading-4 text-gray-700 dark:text-gray-200">{details}</div> : null}
                      <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{entry.user_name || `Пользователь #${entry.user_id}`}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </AccordionSection>
      </div>

      {hasBottomActionBar ? (
        <section
          className="-mx-4 mt-auto border-y border-gray-200 bg-white/98 px-4 py-4 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/98 dark:shadow-[0_-8px_24px_rgba(2,6,23,0.28)] sm:mx-0 sm:rounded-lg sm:border sm:bg-white sm:p-4 sm:shadow-none dark:sm:bg-gray-900"
          style={{ marginBottom: DETAIL_PAGE_BOTTOM_COMPENSATION }}
        >
          <div className="space-y-2">
            {task.can_resolve_deferred ? (
              <>
                <button
                  type="button"
                  onClick={() => { haptic('warning'); setCancelReasonDraft(''); setShowCancelModal(true); }}
                  disabled={actionLoading}
                  className="h-12 w-full rounded-lg border-2 border-red-500 bg-white px-4 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:bg-gray-900 dark:text-red-300 dark:hover:bg-red-950/20"
                >
                  Отменить поручение
                </button>
                <button
                  type="button"
                  onClick={() => { haptic('open'); openDeferredResolveModal(); }}
                  disabled={actionLoading}
                  className="h-12 w-full rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <RotateCcw className="h-4 w-4" />
                    Вернуть в работу
                  </span>
                </button>
              </>
            ) : (
              <>
                {canDeferTask ? (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('open');
                      setDeferDraft({ reason_code: '', comment: '' });
                      setShowDeferModal(true);
                    }}
                    disabled={actionLoading}
                    className="h-12 w-full rounded-lg border-2 border-amber-500 bg-white px-4 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-50 dark:bg-gray-900 dark:text-amber-300 dark:hover:bg-amber-950/20"
                  >
                    Отложить поручение
                  </button>
                ) : null}
                {canCompleteTask ? (
                <button onClick={handleCompleteButtonClick} disabled={actionLoading} aria-disabled={requiresPhotoBeforeComplete && !canForceCompleteWithoutPhoto} className={`h-12 w-full rounded-lg px-4 text-sm font-semibold text-white transition-colors ${requiresPhotoBeforeComplete && !canForceCompleteWithoutPhoto ? 'bg-blue-600/45' : 'bg-blue-600 hover:bg-blue-700'} disabled:opacity-50`}>
                  {actionLoading ? 'Выполняю...' : 'Завершить поручение'}
                </button>
                ) : task.can_take ? (
                  <button onClick={() => runAction(() => takeTask(id), 'Поручение назначено на вас').catch((requestError) => showToast(requestError.message || 'Не удалось взять поручение', 'error'))} disabled={actionLoading} className="h-12 w-full rounded-lg bg-green-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:opacity-50">
                    {actionLoading ? 'Выполняю...' : 'Взять в работу'}
                  </button>
                ) : null}
              </>
            )}
          </div>
        </section>
      ) : null}

      <ModalShell open={showScheduleModal} onClose={() => setShowScheduleModal(false)} variant="window" panelClassName="sm:max-w-md">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 shadow-sm ring-1 ring-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-900/40">
            <Calendar className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Дата и время</h3>
            <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">Измените дату и интервал поручения.</p>
          </div>
        </div>
        <div className="space-y-3">
          <DatePicker label="Дата" value={dispatchForm.scheduled_date} onChange={(value) => setDispatchForm((prev) => ({ ...prev, scheduled_date: value }))} placeholder="Выберите дату" />
          <TimeSlotPicker label="Время" value={dispatchForm.time_slot} onChange={(value) => setDispatchForm((prev) => ({ ...prev, time_slot: value }))} placeholder="Выберите время" />
        </div>
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={() => setShowScheduleModal(false)} className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700">Отмена</button>
          <button type="button" onClick={handleSaveScheduleModal} disabled={actionLoading} className="min-h-[40px] flex-1 rounded-lg bg-emerald-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50">Сохранить</button>
        </div>
      </ModalShell>

      {showAddressModal && (
        <ModalShell
          open={showAddressModal}
          onClose={() => {
            haptic('close');
            setShowAddressModal(false);
            setShowAddressSuggestions(false);
          }}
          variant="window"
          panelClassName="w-full max-w-md p-4"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600 shadow-sm ring-1 ring-sky-100 dark:bg-sky-900/20 dark:text-sky-300 dark:ring-sky-900/40">
                <MapPin className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Сменить адрес</h3>
                <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">Выберите адрес из подсказок, чтобы сохранить координаты.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setShowAddressModal(false);
                setShowAddressSuggestions(false);
              }}
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="relative">
              <FloatingField label="Адрес" disabled={actionLoading}>
                <div className="relative">
                  <input
                    value={dispatchForm.address_text}
                    onChange={(event) => handleAddressChange(event.target.value)}
                    onFocus={() => addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                    disabled={actionLoading}
                    className="w-full rounded-xl bg-white px-4 py-2.5 pr-12 text-[15px] text-gray-900 outline-none placeholder:text-[14px] placeholder:text-gray-400 dark:bg-gray-900 dark:text-gray-100"
                    placeholder="Начните вводить адрес..."
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => handleAddressChange('')}
                      disabled={actionLoading || !dispatchForm.address_text}
                      className={`pointer-events-auto flex h-5 w-5 items-center justify-center transition-colors ${
                        dispatchForm.address_text
                          ? 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
                          : 'pointer-events-none text-transparent'
                      }`}
                      title="Очистить поле"
                      aria-label="Очистить поле"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </FloatingField>

              {showAddressSuggestions && addressSuggestions.length > 0 ? (
                <div className="absolute bottom-full left-0 z-20 mb-1 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-300 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {addressSuggestions.map((suggestion, index) => (
                    <button
                      key={`${getSuggestionAddressText(suggestion)}-${index}`}
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        selectAddressSuggestion(suggestion);
                      }}
                      className="w-full border-b border-gray-100 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900 dark:text-gray-100">{getSuggestionAddressText(suggestion)}</div>
                      {suggestion.data?.city ? <div className="mt-0.5 text-xs text-gray-500">{suggestion.data.city}</div> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setShowAddressModal(false);
                setShowAddressSuggestions(false);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('success');
                handleSaveAddressModal();
              }}
              disabled={actionLoading}
              className="min-h-[40px] flex-1 rounded-lg bg-sky-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-50"
            >
              Сохранить
            </button>
          </div>
        </ModalShell>
      )}
      <AddressWithoutStreetConfirmModal
        suggestion={addressWithoutStreetSuggestion}
        onCancel={() => setAddressWithoutStreetSuggestion(null)}
        onConfirm={() => {
          applyAddressSuggestion(addressWithoutStreetSuggestion);
          setAddressWithoutStreetSuggestion(null);
        }}
      />

      <ModalShell open={showInfoModal} onClose={() => setShowInfoModal(false)} variant="window" panelClassName="sm:max-w-md">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 shadow-sm ring-1 ring-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300 dark:ring-indigo-900/40">
            <Info className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Основная информация</h3>
            <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">Обновите описание и правило обязательного фото.</p>
          </div>
        </div>
        <div className="space-y-3">
          <FloatingField label="Описание поручения">
            <textarea value={dispatchForm.description} onChange={(event) => setDispatchForm((prev) => ({ ...prev, description: event.target.value }))} rows={4} className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-indigo-900/30" placeholder="Описание поручения" />
          </FloatingField>
          <label className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800/70">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Обязательное фото</span>
            <button type="button" onClick={() => setDispatchForm((prev) => ({ ...prev, requires_photo: !prev.requires_photo }))} className="toggle-switch shrink-0" style={{ '--toggle-bg': dispatchForm.requires_photo ? '#4f46e5' : '#d1d5db', '--knob-x': dispatchForm.requires_photo ? '22px' : '2px' }} aria-pressed={dispatchForm.requires_photo}><span className="sr-only">Обязательное фото</span><span className="toggle-switch-knob" /></button>
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={() => setShowInfoModal(false)} className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700">Отмена</button>
          <button type="button" onClick={handleSaveInfoModal} disabled={actionLoading} className="min-h-[40px] flex-1 rounded-lg bg-indigo-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50">Сохранить</button>
        </div>
      </ModalShell>

      {showAssigneeModal && (
        <ModalShell
          open={showAssigneeModal}
          onClose={() => {
            haptic('close');
            setShowAssigneeModal(false);
          }}
          variant="window"
          panelClassName="w-full max-w-md p-4"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 shadow-sm ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40">
                <UserCheck className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Исполнитель поручения</h3>
                <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">Назначьте сотрудника или верните поручение в доступные.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setShowAssigneeModal(false);
              }}
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-3">
            <Dropdown
              label="Исполнитель"
              value={assigneeDraft}
              options={assigneeOptions}
              onChange={(value) => setAssigneeDraft(value ? String(value) : UNASSIGN_TASK_ASSIGNEE_VALUE)}
              placeholder="Выберите исполнителя"
              menuLabel="Исполнители"
              disabled={actionLoading}
              buttonClassName="py-2.5 text-xs"
              itemClassName="py-2.5 text-xs"
            />
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setShowAssigneeModal(false);
              }}
              disabled={actionLoading}
              className="h-10 rounded-lg border-2 border-gray-300 px-3 text-sm font-semibold text-gray-600 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('success');
                handleSaveAssigneeModal();
              }}
              disabled={actionLoading}
              className="h-10 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              Сохранить
            </button>
          </div>
        </ModalShell>
      )}

      <ModalShell open={showDeferModal} onClose={() => setShowDeferModal(false)} variant="sheet" zIndexClass="z-[70]" panelClassName="sm:max-w-lg">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-800">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Отложить поручение</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Исполнитель будет снят, поручение перейдёт диспетчеру.</p>
          </div>
          <button type="button" onClick={() => setShowDeferModal(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <div>
            <Dropdown
              label="Причина"
              value={deferDraft.reason_code}
              onChange={(value) => setDeferDraft((prev) => ({ ...prev, reason_code: String(value) }))}
              options={TASK_DEFERRED_REASON_OPTIONS}
              placeholder="Выберите причину"
              menuLabel="Причины переноса поручения"
            />
          </div>
          <div>
            <FloatingField label="Короткий комментарий">
              <textarea
                value={deferDraft.comment}
                onChange={(event) => setDeferDraft((prev) => ({ ...prev, comment: event.target.value }))}
                rows={3}
                maxLength={300}
                placeholder="Что произошло и что нужно учесть диспетчеру"
                className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-amber-900/30"
              />
            </FloatingField>
            <div className="mt-1 text-right text-[11px] text-gray-400">{deferDraft.comment.length}/300</div>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={() => setShowDeferModal(false)} className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 dark:bg-gray-800 dark:text-gray-100">Назад</button>
            <button
              type="button"
              onClick={handleDeferFromModal}
              disabled={actionLoading || !deferDraft.reason_code || String(deferDraft.comment || '').trim().length < 3}
              className="min-h-[40px] flex-1 rounded-lg bg-amber-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
            >
              {actionLoading ? 'Выполняю...' : 'Отложить'}
            </button>
          </div>
        </div>
      </ModalShell>

      <ModalShell open={showDeferredResolveModal} onClose={() => setShowDeferredResolveModal(false)} variant="window" zIndexClass="z-[70]" panelClassName="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-800">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Вернуть поручение в работу</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Укажите новое время и куда передать поручение.</p>
          </div>
          <button type="button" onClick={() => setShowDeferredResolveModal(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-4">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <DatePicker
                value={deferredResolveDraft.scheduled_date}
                onChange={(value) => setDeferredResolveDraft((prev) => ({ ...prev, scheduled_date: value }))}
                placeholder="Новая дата"
              />
            </div>
            <div>
              <TimeSlotPicker
                value={deferredResolveDraft.time_slot}
                onChange={(value) => setDeferredResolveDraft((prev) => ({ ...prev, time_slot: value }))}
                placeholder="Интервал"
              />
            </div>
          </div>

          <RouteSuggestions
            suggestions={deferredRouteSuggestions}
            loading={deferredRouteSuggestionsLoading}
            onApply={(suggestion) => setDeferredResolveDraft((prev) => ({
              ...prev,
              scheduled_date: suggestion.date_key || '',
              time_slot: suggestion.time_slot || '',
            }))}
            showWhenEmpty
          />

          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Куда вернуть</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'available', label: 'В доступные' },
                { value: 'assigned', label: 'Исполнителю' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDeferredResolveDraft((prev) => ({ ...prev, resolution: option.value, assigned_to: '' }))}
                  className={`rounded-xl border px-3 py-3 text-sm font-medium ${deferredResolveDraft.resolution === option.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300'
                    : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {deferredResolveDraft.resolution === 'assigned' ? (
            <div>
              <Dropdown
                label="Исполнитель"
                value={deferredResolveDraft.assigned_to}
                onChange={(value) => setDeferredResolveDraft((prev) => ({ ...prev, assigned_to: String(value) }))}
                options={deferredAssigneeOptions}
                placeholder="Выберите исполнителя"
                menuLabel="Исполнители поручения"
              />
            </div>
          ) : null}

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={() => setShowDeferredResolveModal(false)} className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 dark:bg-gray-800 dark:text-gray-100">Назад</button>
            <button
              type="button"
              onClick={handleResolveDeferred}
              disabled={actionLoading || !deferredResolveDraft.scheduled_date || !deferredResolveDraft.time_slot || (deferredResolveDraft.resolution === 'assigned' && !deferredResolveDraft.assigned_to)}
              className="min-h-[40px] flex-1 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {actionLoading ? 'Выполняю...' : 'Вернуть в работу'}
            </button>
          </div>
        </div>
      </ModalShell>

      <ModalShell open={showCancelModal} onClose={() => setShowCancelModal(false)} variant="window" panelClassName="sm:max-w-md">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600 shadow-sm ring-1 ring-red-100 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-900/40">
            <XCircle className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Отменить поручение</h3>
            <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">Причина попадёт в историю действий.</p>
          </div>
        </div>
        <FloatingField label="Причина отмены">
          <textarea value={cancelReasonDraft} onChange={(event) => setCancelReasonDraft(event.target.value)} rows={4} maxLength={300} className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-red-900/30" placeholder="Укажите причину отмены" />
        </FloatingField>
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={() => setShowCancelModal(false)} className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700">Назад</button>
          <button type="button" onClick={handleCancelFromModal} disabled={actionLoading || String(cancelReasonDraft || '').trim().length < 3} className="min-h-[40px] flex-1 rounded-lg bg-red-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50">Отменить</button>
        </div>
      </ModalShell>

      <ModalShell open={showCommentModal} onClose={() => setShowCommentModal(false)} variant="sheet" zIndexClass="z-[70]" panelClassName="sm:max-w-lg">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-800">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Новый комментарий</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Добавится в историю поручения</p>
          </div>
        </div>
        <div className="p-4">
          <FloatingField label="Комментарий">
            <textarea value={commentText} onChange={(event) => setCommentText(event.target.value)} rows={4} autoFocus placeholder="Напишите комментарий..." className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100" />
          </FloatingField>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => setShowCommentModal(false)} className="h-10 flex-1 rounded-lg bg-gray-100 px-4 text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200">Отмена</button>
            <button type="button" onClick={handleComment} disabled={actionLoading || !String(commentText || '').trim()} className="h-10 flex-1 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white disabled:opacity-50">Добавить</button>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        open={showPhotoModal}
        onClose={() => {
          if (actionLoading || isUploadingPhoto || uploadingPhotoRef.current) return;
          setShowPhotoModal(false);
        }}
        variant="sheet"
        zIndexClass="z-[70]"
        panelClassName="sm:max-w-lg"
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-800">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Добавить фото</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Фото будет привязано к поручению</p>
          </div>
          <button
            type="button"
            disabled={actionLoading || isUploadingPhoto}
            onClick={() => {
              if (actionLoading || isUploadingPhoto || uploadingPhotoRef.current) return;
              setShowPhotoModal(false);
            }}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <label className={`relative flex min-h-[92px] items-center justify-center gap-3 rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950/20 dark:text-blue-300 ${actionLoading || isUploadingPhoto ? 'pointer-events-none' : 'cursor-pointer'}`}>
            {photoPreviewUrl ? (
              <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-900">
                <img
                  src={photoPreviewUrl}
                  alt="Превью выбранного фото"
                  className={`h-full w-full object-cover transition-all ${actionLoading || isUploadingPhoto ? 'filter blur-[1px] brightness-50 grayscale-[30%]' : ''}`}
                />
                {(actionLoading || isUploadingPhoto) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <CircularProgress
                      progress={uploadProgress}
                      size={38}
                      strokeWidth={3}
                      indicatorColor="text-white"
                      trackColor="text-white/25"
                      showPercent={true}
                    />
                  </div>
                )}
              </div>
            ) : (
              <FileImage className="h-6 w-6 shrink-0" />
            )}
            <span className="min-w-0 text-left">
              <span className="block truncate font-medium">{photoFiles[0]?.name || 'Выбрать фото или снять камерой'}</span>
              {actionLoading || isUploadingPhoto ? (
                <span className="mt-1 block text-xs font-semibold text-blue-600 dark:text-blue-400">
                  {typeof uploadProgress === 'number' && uploadProgress < 100
                    ? `Загрузка на сервер: ${uploadProgress}%`
                    : 'Сохранение в хранилище...'}
                </span>
              ) : (
                <span className="mt-1 block text-xs opacity-80">До 12 МБ (JPG, PNG, WEBP)</span>
              )}
            </span>
            {photoFiles[0] && !actionLoading && !isUploadingPhoto ? (
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setPhotoFiles([]);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-gray-500 shadow-sm hover:bg-white hover:text-red-600 dark:bg-gray-900/90 dark:text-gray-300 dark:hover:text-red-400"
                aria-label="Убрать выбранное фото"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              disabled={actionLoading || isUploadingPhoto}
              onChange={(event) => {
                const nextFile = event.target.files?.[0] || null;
                setPhotoFiles(nextFile ? [nextFile] : []);
              }}
              className="hidden"
            />
          </label>
          <FloatingField label="Подпись">
            <input
              type="text"
              value={photoCaption}
              disabled={actionLoading || isUploadingPhoto}
              onChange={(event) => setPhotoCaption(event.target.value)}
              placeholder="Например: выполнено на месте"
              className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-blue-500 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
            />
          </FloatingField>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              type="button"
              disabled={actionLoading || isUploadingPhoto}
              onClick={() => {
                if (actionLoading || isUploadingPhoto || uploadingPhotoRef.current) return;
                setShowPhotoModal(false);
              }}
              className="h-9 rounded-lg border-2 border-gray-300 px-3 text-sm font-medium text-gray-700 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={handlePhotoUpload}
              disabled={actionLoading || isUploadingPhoto || !photoFiles.length}
              className="flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {actionLoading || isUploadingPhoto ? (
                <>
                  <CircularProgress size={16} strokeWidth={2.5} showPercent={false} indicatorColor="text-white" trackColor="text-white/30" />
                  <span>{typeof uploadProgress === 'number' && uploadProgress < 100 ? `Загрузка ${uploadProgress}%` : 'Сохраняю...'}</span>
                </>
              ) : 'Добавить'}
            </button>
          </div>
        </div>
      </ModalShell>

      <ModalShell open={showCompleteOverrideModal} onClose={() => setShowCompleteOverrideModal(false)} variant="window" panelClassName="sm:max-w-md">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600 shadow-sm ring-1 ring-violet-100 dark:bg-violet-900/20 dark:text-violet-300 dark:ring-violet-900/40">
            <Shield className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Закрыть без фото</h3>
            <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
              {dispatcherCompletesForAssignee
                ? `Под диспетчерским доступом поручение закроется без фото. В статистику выполнение пойдёт сотруднику ${dispatcherCompleteCreditLabel}, а в истории останется, что закрыли вы.`
                : 'Под диспетчерским доступом поручение можно закрыть без фото. Выполнение всё равно будет засчитано исполнителю.'}
            </p>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={() => setShowCompleteOverrideModal(false)} className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700">Назад</button>
          <button type="button" onClick={async () => { await handleComplete(true); setShowCompleteOverrideModal(false); }} disabled={actionLoading} className="min-h-[40px] flex-1 rounded-lg bg-violet-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-violet-700 disabled:opacity-50">Закрыть без фото</button>
        </div>
      </ModalShell>

      <ModalShell open={showCompleteConfirmModal} onClose={() => setShowCompleteConfirmModal(false)} variant="window" panelClassName="sm:max-w-md">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 shadow-sm ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Подтвердить закрытие</h3>
            <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
              {dispatcherCompletesForAssignee
                ? `Поручение будет отмечено как выполненное. В статистику оно пойдёт сотруднику ${dispatcherCompleteCreditLabel}, а в истории останется, что закрыли вы через диспетчерский доступ.`
                : 'Поручение будет отмечено как выполненное.'}
            </p>
          </div>
        </div>
        {dispatcherCompletesForAssignee ? (
          <div className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-800 dark:border-violet-900 dark:bg-violet-950/30 dark:text-violet-200">
            <div className="flex items-start gap-2">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Закрытие будет выполнено диспетчером, но засчитано сотруднику {dispatcherCompleteCreditLabel}.</span>
            </div>
          </div>
        ) : null}
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={() => setShowCompleteConfirmModal(false)} className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700">Отмена</button>
          <button type="button" onClick={async () => { await handleComplete(false); setShowCompleteConfirmModal(false); }} disabled={actionLoading} className="min-h-[40px] flex-1 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50">Закрыть поручение</button>
        </div>
      </ModalShell>

      {selectedPhoto ? ReactDOM.createPortal(
        <div className="fixed inset-0 z-[80] flex flex-col bg-black/95 text-white">
          <div className="flex items-center justify-between gap-3 p-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">Фото поручения</div>
              <div className="truncate text-xs text-white/70">{selectedPhoto.caption || selectedPhoto.original_filename || selectedPhoto.uploader_name || 'Без подписи'}</div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={handleDownloadSelectedPhoto}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white"
                title="Скачать фото"
                aria-label="Скачать фото"
              >
                <Download className="h-4 w-4" />
              </button>
              {canDeletePhotos && (
                <button
                  type="button"
                  onClick={() => {
                    haptic('warning');
                    setPhotoToDelete(selectedPhoto);
                  }}
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white hover:bg-red-500/20 hover:text-red-300"
                  title="Удалить фото"
                  aria-label="Удалить фото"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <button type="button" onClick={() => { haptic('close'); setSelectedPhoto(null); }} className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-2 select-none">
            <ProtectedImage
              src={getTaskPhotoUrl(selectedPhoto, 'file')}
              thumbnailSrc={getTaskPhotoUrl(selectedPhoto, 'thumbnail')}
              showLoader={true}
              loaderText="Загрузка оригинала..."
              alt={selectedPhoto.caption || selectedPhoto.original_filename || 'Фото поручения'}
              className="max-h-full max-w-full object-contain"
              draggable={false}
            />
          </div>
        </div>,
        document.body
      ) : null}

      <ConfirmModal
        isOpen={Boolean(photoToDelete)}
        title="Удалить фото безвозвратно?"
        message="Фотография будет удалена с сервера безвозвратно. Это действие нельзя отменить."
        confirmText="Удалить безвозвратно"
        cancelText="Отмена"
        type="danger"
        onConfirm={async () => {
          const photo = photoToDelete;
          await handleDeleteTaskPhoto(photo);
        }}
        onCancel={() => setPhotoToDelete(null)}
      />

      {task.completed_by_actor && task.completed_by_actor !== task.completed_by ? <div className="rounded-3xl border border-violet-200 bg-violet-50 px-4 py-4 text-sm text-violet-800 dark:border-violet-900 dark:bg-violet-950/30 dark:text-violet-200"><div className="flex items-start gap-3"><ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" /><div>Поручение засчитано сотруднику {task.completed_by_name || `#${task.completed_by}`}, но закрыто пользователем {task.completed_by_actor_name || `#${task.completed_by_actor}`}.</div></div></div> : null}
      
    </div>
  );
};

export default TaskDetail;
